import {
  ButtonComponentActionResultTypenames,
  EventType,
  OfferSource,
  PartnerOfferDetails as PartnerOfferDetailsV2Type,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams, useRouter } from 'expo-router';
import MerchantDetailsV2 from 'features/rewards/Details/PartnerOfferDetailsV2/MerchantDetailsV2/MerchantDetailsV2';
import RenderHtmlOfferSectionView from 'features/rewards/Details/RenderHtmlOfferSectionView/RenderHtmlOfferSectionView';
import { openMap } from 'features/rewards/helpers/openMap.helpers';
import useKardAttribution from 'features/rewards/hooks/useKardAttribution';
import usePartnerOffersDetails from 'features/rewards/hooks/usePartnerOfferDetails';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseOptionalGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { openExternalUrl } from 'services/deeplinks/helpers/openExternalUrl';
import Button from 'shared/components/vxl/Button/Button';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { isWeb } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

interface PartnerOfferDetailsV2Props {
  id: string;
  sectionName?: string;
}

export enum KardMedium {
  BROWSE = 'BROWSE',
  MAP = 'MAP',
  SEARCH = 'SEARCH',
}

const Divider = () => <View style={styles.dividerStyle} />;

const getHtmlContents = (offer: PartnerOfferDetailsV2Type) => {
  let disclaimerHtml = offer.disclaimer?.replace(/\n/g, '<br />');
  if (offer.legalUrl && offer.legalText) {
    disclaimerHtml += `&nbsp;<a href="${offer.legalUrl}" target="_blank">${
      offer.legalText ?? ''
    }</a>`;
  }

  return [
    {
      id: 'about',
      title: offer.aboutTitle,
      content: offer.aboutContent ? `<p>${offer.aboutContent}</p>` : undefined,
      showDivider: true,
      linkText: undefined,
    },
    {
      id: 'disclaimer',
      title: offer.bodySection3Title,
      content: disclaimerHtml,
      showDivider: false,
      linkText: offer.legalText,
    },
  ];
};

const hasValidCoordinates = (offer: PartnerOfferDetailsV2Type) => {
  return (
    offer.coordinates !== null &&
    offer.coordinates?.latitude !== null &&
    offer.coordinates?.longitude !== null &&
    offer.coordinates?.latitude !== 0 &&
    offer.coordinates?.longitude !== 0
  );
};

const PartnerOfferDetailsV2 = ({ id }: PartnerOfferDetailsV2Props) => {
  const insets = useSafeAreaInsets();

  const router = useRouter();
  const params = useLocalSearchParams<{
    lat?: string;
    lng?: string;
  }>();

  const { lat, lng } = params;

  const parsedLat = lat ? parseFloat(lat) : NaN;
  const parsedLng = lng ? parseFloat(lng) : NaN;

  const overrideCoordinates =
    !isNaN(parsedLat) && !isNaN(parsedLng)
      ? { latitude: parsedLat, longitude: parsedLng }
      : undefined;

  const { loading, offer, error, stale } = usePartnerOffersDetails(
    id,
    overrideCoordinates,
  );
  const isEmptyData = !loading && !error && !offer;

  const { recordImpressions, formatOfferViewData } = useKardAttribution();
  const viewEventTrackedForId = useRef<string | null>(null);

  useEffect(() => {
    if (
      offer &&
      offer.offerSource === OfferSource.Kard &&
      viewEventTrackedForId.current !== offer.id
    ) {
      const viewData = formatOfferViewData(offer.id, KardMedium.BROWSE);
      if (!viewData) {
        return;
      }
      viewEventTrackedForId.current = offer.id;
      void recordImpressions(EventType.View, viewData);
    }
  }, [offer, formatOfferViewData, recordImpressions]);

  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'partnerOffersDetails',
  });

  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();

  const displayAnalytics = parseOptionalGQLAnalyticsEvent(
    offer?.displayAnalytics,
  );
  useViewDidFocusLogScreenAnalytics(displayAnalytics);

  const howItWorks = offer?.howItWorks;

  const { segmentClient } = useAnalyticsConfiguration();
  useEffect(() => {
    if (lat || lng) {
      const screenName = displayAnalytics?.name ?? 'PartnerOfferDetails';
      const locationAttributes = {
        ...displayAnalytics?.properties,
        latitude: lat ? parseFloat(lat) : undefined,
        longitude: lng ? parseFloat(lng) : undefined,
      };
      void segmentClient.logScreenEvent(screenName, locationAttributes);
    }
  }, [lat, lng, displayAnalytics, segmentClient]);

  const logErrorAnalytics = usePressableAnalytics({
    analyticsId: 'partnerOfferPageError',
    customAttributes: {
      error_description: error?.message ?? '',
    },
  });

  useEffect(() => {
    if (error) {
      logErrorAnalytics();
    }
  }, [error, logErrorAnalytics]);

  const openUrlNavigationAnalyticsProperties =
    offer?.primaryButton?.buttonOnClick.__typename ===
      ButtonComponentActionResultTypenames.OpenURLNavigationAction &&
    offer?.primaryButton?.buttonOnClick?.analyticsEvent
      ? (parseOptionalGQLAnalyticsEvent(
          offer.primaryButton.buttonOnClick.analyticsEvent,
        )?.properties ?? null)
      : null;

  const openUrlNavigationAnalytics = usePressableAnalytics({
    analyticsId: 'offerGetDirectionClicked',
    customAttributes: openUrlNavigationAnalyticsProperties,
  });

  const handlePdfClick = () => {
    if (offer?.legalUrl) {
      router.push({
        pathname: '/rewards/disclosure',
        params: {
          pdfUrl: offer.legalUrl,
        },
      });
    }
  };

  const fotterButtons = [
    offer?.primaryButton
      ? {
          id: offer.primaryButton.id,
          label: offer.primaryButton.buttonText,
          type: offer.primaryButton.buttonStyle,
          onPress: async () => {
            if (
              offer.primaryButton?.buttonOnClick.__typename ===
              ButtonComponentActionResultTypenames.OpenURLNavigationAction
            ) {
              openUrlNavigationAnalytics();
              await openExternalUrl(offer.primaryButton?.buttonOnClick.url);
            }
          },
        }
      : null,
    offer?.secondaryButton
      ? {
          id: offer.secondaryButton.id,
          label: offer.secondaryButton.buttonText,
          type: offer.secondaryButton.buttonStyle,
          onPress: async () => {
            if (
              offer.secondaryButton?.buttonOnClick.__typename ===
              ButtonComponentActionResultTypenames.OpenURLNavigationAction
            ) {
              openUrlNavigationAnalytics();

              if (hasValidCoordinates(offer) && offer.coordinates) {
                const { latitude, longitude } = offer.coordinates;
                await openMap(latitude, longitude);
              } else {
                await openExternalUrl(offer.secondaryButton?.buttonOnClick.url);
              }
            }
          },
        }
      : null,
  ].filter((button) => button !== null);

  return (
    <View style={[styles.viewContainer, { paddingBottom: insets.bottom }]}>
      {!isWeb ? (
        <NavigationBar
          title=""
          primaryIcon={<Icon iconKey="CLOSE" iconSize="MD" />}
          primaryOnPress={() => router.back()}
        />
      ) : null}
      <View style={styles.modalContainer}>
        <ScrollView>
          {offer ? (
            <View style={styles.container}>
              <View style={styles.headerContainer}>
                <View>
                  <MerchantDetailsV2 {...offer} />
                  <Divider />
                  {howItWorks?.steps ? (
                    <>
                      <View style={styles.howItWorksContainer}>
                        <View style={styles.titleContainer}>
                          <SectionDivider
                            labelText={howItWorks.title ?? null}
                            textSize="large"
                          />
                        </View>
                        <View style={styles.bodyContainer}>
                          <BodyBaseText>{howItWorks.steps}</BodyBaseText>
                        </View>
                      </View>
                      <Divider />
                    </>
                  ) : null}
                  {getHtmlContents(offer).map((item) => {
                    if (!item.content) {
                      return null;
                    }
                    return (
                      <RenderHtmlOfferSectionView
                        id={item.id}
                        key={item.title}
                        content={item.content}
                        titleText={item.title}
                        showDivider={item.showDivider}
                        linkText={item.linkText as string}
                        handlePdfClick={handlePdfClick}
                      />
                    );
                  })}
                </View>
              </View>
            </View>
          ) : null}
        </ScrollView>
        {fotterButtons.map((button) => (
          <View key={button.id} style={styles.buttonContainer}>
            <Button
              label={button.label}
              size="LARGE"
              type={button.type}
              width="FULL"
              onPress={button.onPress}
            />
          </View>
        ))}
        <ScreenTransitionOverlay
          LoadingComponent={
            <View style={{ paddingTop: insets.top }}>
              <LoadingSpinner />
            </View>
          }
          isLoading={loading || stale}
          ErrorComponent={
            error || isEmptyData ? (
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={
                  isEmptyData ? translatedText('notFoundTitle') : defaultTitle
                }
                messageBody={
                  isEmptyData
                    ? translatedText('notFoundMessage')
                    : defaultMessage
                }
                primaryButton={{
                  title: cancelButtonTitle,
                  onPress: () => router.back(),
                }}
              />
            ) : null
          }
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
    width: '100%',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    width: '100%',
  },
  dividerStyle: {
    backgroundColor: theme.color.background.surface,
    padding: theme.spacing.xs,
  },
  headerContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    width: '100%',
  },
  howItWorksContainer: {
    flexDirection: 'column',
    gap: theme.spacing.s,
    paddingTop: theme.spacing.s,
    paddingBottom: theme.spacing.l,
  },
  modalContainer: {
    flex: 1,
  },
  titleContainer: {
    paddingTop: theme.spacing.m,
  },
  bodyContainer: {
    paddingBottom: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  viewContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    ...Platform.select({
      web: {
        maxWidth: 600,
      },
    }),
    alignSelf: 'center',
    width: '100%',
  },
});

export default PartnerOfferDetailsV2;
